import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Modal from '../Layout/Modal';

const LeaderboardIntro = styled.p`
  margin: 0 0 1rem 0;
  color: ${props => props.theme.textSecondary};
  font-size: 0.9rem;
  line-height: 1.5;
`;

const FiltersBar = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin: 1rem 0;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme.border};
`;

const PaginationControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.disabled ? props.theme.backgroundSecondary : props.theme.cardBackground};
  color: ${props => props.disabled ? props.theme.textSecondary : props.theme.text};
  border-radius: 999px;
  padding: 0.45rem 0.8rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.55 : 1};

  &:hover {
    border-color: ${props => props.disabled ? props.theme.border : props.theme.buttonPrimary};
  }
`;

const PaginationInfo = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
`;

const FilterGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: ${props => props.theme.textSecondary};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilterSelect = styled.select`
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  border-radius: 10px;
  padding: 0.7rem 0.85rem;
  font-size: 0.9rem;
  outline: none;

  &:focus {
    border-color: ${props => props.theme.buttonPrimary};
  }
`;

const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const LeaderboardRow = styled.div`
  display: grid;
  grid-template-columns: 48px minmax(0, 1.4fr) repeat(5, minmax(0, 0.8fr));
  gap: 0.75rem;
  align-items: center;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 1px solid ${props => props.$highlight ? '#c9a227' : props.theme.border};
  background: ${props => props.$highlight ? 'linear-gradient(135deg, rgba(212, 175, 55, 0.28), rgba(212, 175, 55, 0.12))' : props.theme.backgroundSecondary};
  box-shadow: ${props => props.$highlight ? '0 0 0 1px rgba(212, 175, 55, 0.3) inset' : 'none'};

  @media (max-width: 900px) {
    grid-template-columns: 44px minmax(0, 1fr);
  }
`;

const RankPill = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: ${props => props.$highlight ? '#3a2c00' : props.theme.text};
  background: ${props => props.$highlight ? '#d4af37' : props.theme.cardBackground};
`;

const NameBlock = styled.div`
  min-width: 0;
`;

const LeaderName = styled.div`
  color: ${props => props.$highlight ? '#3a2c00' : props.theme.text};
  font-weight: 700;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CurrentUserTag = styled.span`
  display: inline-flex;
  margin-top: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: #d4af37;
  color: #3a2c00;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const StatCell = styled.div`
  color: ${props => props.$highlight ? '#3a2c00' : props.theme.textSecondary};
  font-size: 0.82rem;
  text-align: right;

  strong {
    display: block;
    color: ${props => props.$highlight ? '#3a2c00' : props.theme.text};
    font-size: 0.95rem;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const MobileStatCell = styled.div`
  color: ${props => props.$highlight ? '#3a2c00' : props.theme.textSecondary};
  font-size: 0.8rem;
  text-align: left;

  strong {
    display: block;
    color: ${props => props.$highlight ? '#3a2c00' : props.theme.text};
    font-size: 0.95rem;
  }
`;

const EmptyState = styled.div`
  padding: 1.5rem;
  border: 1px dashed ${props => props.theme.border};
  border-radius: 12px;
  color: ${props => props.theme.textSecondary};
  text-align: center;
`;

const LeaderboardSummary = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.82rem;
  margin: 0 0 1rem 0;
`;

const ErrorState = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 10px;
  background: rgba(220, 53, 69, 0.12);
  border: 1px solid rgba(220, 53, 69, 0.28);
  color: ${props => props.theme.text};
`;

const MobileStatGrid = styled.div`
  display: none;
  margin-top: 0.85rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;

  @media (max-width: 900px) {
    display: grid;
  }
`;

const sortAccessors = {
  rank: (entry) => entry.rank,
  points: (entry) => entry.totalPoints,
  hours: (entry) => entry.hoursStudied,
  currentStreak: (entry) => entry.currentStreakDays,
  topStreak: (entry) => entry.longestStreakDays,
  badges: (entry) => entry.badgesEarned,
  name: (entry) => entry.displayName.toLowerCase()
};

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 50, 75, 100, 150, 200];

const LeaderboardModal = ({ isOpen, onClose, leaderboard = [], loading = false, error = '' }) => {
  const [sortBy, setSortBy] = useState('points');
  const [sortDirection, setSortDirection] = useState('desc');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const sortedLeaderboard = useMemo(() => {
    const accessor = sortAccessors[sortBy] || sortAccessors.points;
    const directionFactor = sortDirection === 'asc' ? 1 : -1;

    return [...leaderboard].sort((left, right) => {
      const leftValue = accessor(left);
      const rightValue = accessor(right);

      if (typeof leftValue === 'string' || typeof rightValue === 'string') {
        return String(leftValue).localeCompare(String(rightValue)) * directionFactor;
      }

      return (Number(leftValue) - Number(rightValue)) * directionFactor;
    });
  }, [leaderboard, sortBy, sortDirection]);

  const sortedWithRanks = useMemo(() => {
    return sortedLeaderboard.map((entry, index) => ({
      ...entry,
      rank: index + 1
    }));
  }, [sortedLeaderboard]);

  const totalPages = Math.max(1, Math.ceil(sortedWithRanks.length / pageSize));

  const pagedLeaderboard = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedWithRanks.slice(startIndex, startIndex + pageSize);
  }, [sortedWithRanks, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy, sortDirection, pageSize, leaderboard.length]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const goToPage = (nextPage) => {
    const safePage = Math.min(Math.max(nextPage, 1), totalPages);
    setCurrentPage(safePage);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Study Leaderboard" size="xlarge">
      <LeaderboardIntro>
        Rankings are based on study activity. Your row is highlighted in gold.
      </LeaderboardIntro>
      <LeaderboardSummary>
        Tip: use the filters below to sort by points, hours studied, streaks, or badges. The list updates instantly.
      </LeaderboardSummary>

      <FiltersBar>
        <FilterGroup>
          Sort By
          <FilterSelect value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
            <option value="points">Points</option>
            <option value="hours">Hours studied</option>
            <option value="currentStreak">Current streak</option>
            <option value="topStreak">Top streak</option>
            <option value="badges">Badges earned</option>
            <option value="name">Name</option>
            <option value="rank">Rank</option>
          </FilterSelect>
        </FilterGroup>
        <FilterGroup>
          Order
          <FilterSelect value={sortDirection} onChange={(event) => setSortDirection(event.target.value)}>
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </FilterSelect>
        </FilterGroup>
      </FiltersBar>

      <PaginationControls style={{ marginBottom: '1rem' }}>
        <FilterGroup style={{ minWidth: '140px' }}>
          People per page
          <FilterSelect value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </FilterSelect>
        </FilterGroup>
      </PaginationControls>

      {error ? <ErrorState>{error}</ErrorState> : null}

      {loading && leaderboard.length === 0 ? (
        <EmptyState>Loading leaderboard...</EmptyState>
      ) : pagedLeaderboard.length > 0 ? (
        <LeaderboardList>
          {pagedLeaderboard.map((entry) => (
            <LeaderboardRow key={`${entry.rank}-${entry.displayName}`} $highlight={entry.isCurrentUser}>
              <RankPill $highlight={entry.isCurrentUser}>{entry.rank}</RankPill>
              <NameBlock>
                <LeaderName $highlight={entry.isCurrentUser}>{entry.displayName}</LeaderName>
                {entry.isCurrentUser ? <CurrentUserTag>You</CurrentUserTag> : null}
                <MobileStatGrid>
                  <MobileStatCell $highlight={entry.isCurrentUser}>
                    <strong>{entry.totalPoints}</strong>
                    Points
                  </MobileStatCell>
                  <MobileStatCell $highlight={entry.isCurrentUser}>
                    <strong>{entry.hoursStudied}</strong>
                    Hours Studied
                  </MobileStatCell>
                  <MobileStatCell $highlight={entry.isCurrentUser}>
                    <strong>{entry.currentStreakDays}</strong>
                    Current Streak
                  </MobileStatCell>
                  <MobileStatCell $highlight={entry.isCurrentUser}>
                    <strong>{entry.longestStreakDays}</strong>
                    Top Streak
                  </MobileStatCell>
                  <MobileStatCell $highlight={entry.isCurrentUser}>
                    <strong>{entry.badgesEarned}</strong>
                    Badges
                  </MobileStatCell>
                </MobileStatGrid>
              </NameBlock>
              <StatCell $highlight={entry.isCurrentUser}>
                <strong>{entry.totalPoints}</strong>
                Points
              </StatCell>
              <StatCell $highlight={entry.isCurrentUser}>
                <strong>{entry.hoursStudied}</strong>
                Hours Studied
              </StatCell>
              <StatCell $highlight={entry.isCurrentUser}>
                <strong>{entry.currentStreakDays}</strong>
                Current Streak
              </StatCell>
              <StatCell $highlight={entry.isCurrentUser}>
                <strong>{entry.longestStreakDays}</strong>
                Top Streak
              </StatCell>
              <StatCell $highlight={entry.isCurrentUser}>
                <strong>{entry.badgesEarned}</strong>
                Badges
              </StatCell>
            </LeaderboardRow>
          ))}
        </LeaderboardList>
      ) : (
        <EmptyState>No leaderboard data available yet.</EmptyState>
      )}

      {sortedWithRanks.length > 0 && (
        <PaginationBar>
          <PaginationControls>
            <PaginationButton type="button" onClick={() => goToPage(1)} disabled={currentPage === 1}>
              First
            </PaginationButton>
            <PaginationButton type="button" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
              Prev
            </PaginationButton>
            <PaginationInfo>
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, sortedWithRanks.length)} of {sortedWithRanks.length}
            </PaginationInfo>
            <PaginationButton type="button" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
              Next
            </PaginationButton>
            <PaginationButton type="button" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}>
              Last
            </PaginationButton>
          </PaginationControls>
        </PaginationBar>
      )}
    </Modal>
  );
};

export default LeaderboardModal;
